import {
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { IconComponent } from '../../../shared/components/icon/icon.component';
import { FocusTrapDirective } from '../../../shared/directives/focus-trap/focus-trap.directive';
import { logHttpError } from '../../../shared/utils/logging/http-error-logging.utils';
import { PatientPortalProxyUserRequestsService } from '../../services/patient-portal-proxy-user-requests.service';

/**
 * Reason codes sent as `patientPortalProxyUserRequestDenialReasonTypeId`.
 *
 * The ids mirror the admin API's `PatientPortalProxyUserRequestDenialReasonType`
 * enum (seeded 1–5 in `usr.PatientPortalProxyUserRequestDenialReasonType`), which
 * the deny endpoint validates against — an id outside it is rejected, and a wrong
 * one inside it would write a wrong reason to the audit record. The set is *not*
 * the linking queue's: proxy denials add a relationship reason and an
 * authorization one, which shifts fraud and duplicate up by one. Keep this list
 * and that enum in step.
 */
const PROXY_DENIAL_REASONS = [
  { id: 1, label: 'Cannot verify proxy identity' },
  { id: 2, label: 'Cannot verify relationship to patient' },
  { id: 3, label: 'Missing or invalid authorization' },
  { id: 4, label: 'Suspected fraudulent or unauthorized request' },
  { id: 5, label: 'Duplicate or existing proxy relationship' },
] as const;

@Component({
  selector: 'app-proxy-deny-request-modal',
  standalone: true,
  imports: [IconComponent, FocusTrapDirective],
  templateUrl: './proxy-deny-request-modal.html',
  styleUrl: './proxy-deny-request-modal.scss',
})
export class ProxyDenyRequestModal implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly requestsService = inject(PatientPortalProxyUserRequestsService);

  readonly requestId = input.required<number>();
  /** Concurrency token from the detail payload — a GUID here, not a number. */
  readonly version = input.required<string>();
  readonly patientName = input.required<string>();
  readonly proxyName = input.required<string>();

  readonly cancelled = output<void>();
  readonly denied = output<void>();

  readonly denialReasonId = signal<number | null>(PROXY_DENIAL_REASONS[0].id);
  readonly denyNoteText = signal('');
  readonly isDenyingRequest = signal(false);
  readonly denyError = signal<string | null>(null);

  readonly denialReasons = PROXY_DENIAL_REASONS;

  /** Denial is only allowed once a reason is chosen and no request is in flight. */
  readonly canSubmit = computed(() => this.denialReasonId() !== null && !this.isDenyingRequest());

  ngOnInit(): void {
    setTimeout(() => {
      this.elementRef.nativeElement.querySelector<HTMLElement>('input[type="radio"]')?.focus();
    });
  }

  cancel(): void {
    this.cancelled.emit();
  }

  updateDenialReason(id: number): void {
    this.denialReasonId.set(id);
  }

  updateDenyNote(value: string): void {
    this.denyNoteText.set(value);
  }

  submit(): void {
    const denialReasonId = this.denialReasonId();
    if (this.isDenyingRequest() || denialReasonId === null) return;

    this.isDenyingRequest.set(true);
    this.denyError.set(null);

    this.requestsService
      .denyRequest(this.requestId(), {
        patientPortalProxyUserRequestDenialReasonTypeId: denialReasonId,
        note: this.denyNoteText().trim() || null,
        version: this.version(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isDenyingRequest.set(false);
          this.denied.emit();
        },
        error: (error) => {
          logHttpError('Failed to deny proxy request', error);
          this.denyError.set('Unable to deny this request. Please try again.');
          this.isDenyingRequest.set(false);
        },
      });
  }

  trackByDenialReasonId(_: number, reason: { id: number }): number {
    return reason.id;
  }
}
